import { Dispatch, SetStateAction } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'src/lib'
import { OperacionContraparteCuentaFields } from '../types/fields'
import { zodResolver } from '@hookform/resolvers/zod'
import { HttpContraparte } from '../api/httpContraparte'
import toast from 'react-hot-toast'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputLabel, Stack } from '@mui/material'
import { AsyncSelect, ControlledTextField } from 'src/components/forms'
import { HttpCacheOperacion } from 'src/pages/sistema/api/httpCacheOperacion'
import { OperacionContraparteImporteFields } from 'src/pages/sistema/types/importes'
import { HttpCacheGeneral } from 'src/http/httpCacheGeneral'
import { TextField } from 'src/lib/itaptec/components_forms/src'

const schemaNuevoDetalle = z.object({
    [OperacionContraparteCuentaFields.NroCuenta]: z.string(),
    [OperacionContraparteCuentaFields.RazonSocial]: z.string(),
    [OperacionContraparteCuentaFields.CodMoneda]: z.number(),
    [OperacionContraparteCuentaFields.ImporteConcertacion]: z.number(),
    [OperacionContraparteCuentaFields.ImporteDPV]: z.number(),
    [OperacionContraparteCuentaFields.ImporteFOP]: z.number()
})

type FormNuevoDetalle = z.infer<typeof schemaNuevoDetalle>

type DialogNuevoDetalleProps = {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    idOperacion: number
    idPais: number
    refetch: () => void
}

export const DialogNuevoDetalle = ({ open, idOperacion, setOpen, refetch }: DialogNuevoDetalleProps) => {
    const { control, handleSubmit, watch } = useForm<FormNuevoDetalle>({
        resolver: zodResolver(schemaNuevoDetalle),
        defaultValues: {
            [OperacionContraparteCuentaFields.CodMoneda]: 1
        }
    })

    const alGuardarDetalle = (data: FormNuevoDetalle) => {
        const promise = HttpContraparte.postCuenta(idOperacion, data)

        toast
            .promise(promise, {
                loading: 'Guardando',
                success: 'Guardado correctamente',
                error: 'Error al guardar'
            })
            .then(() => {
                refetch()
                setOpen(false)
            })
    }

    const monedaSeleccionada = watch(OperacionContraparteCuentaFields.CodMoneda)

    return (
        <Dialog open={open} maxWidth='md' fullWidth>
            <DialogTitle>Nueva cuenta</DialogTitle>
            <DialogContent>
                <Stack spacing={4} pt={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                control={control}
                                name={OperacionContraparteCuentaFields.NroCuenta}
                                label='Nro Cuenta'
                                fullWidth
                                mask={{
                                    mask: Number
                                }}
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                control={control}
                                name={OperacionContraparteCuentaFields.RazonSocial}
                                label='Razón Social'
                                fullWidth
                                mask={{
                                    mask: /^[A-Za-z]+[A-Za-z ]*$/
                                }}
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AsyncSelect
                                control={control}
                                name={OperacionContraparteCuentaFields.CodMoneda}
                                label='Moneda'
                                loadOptions={() => HttpCacheGeneral.getMonedas()}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <InputLabel>Límites</InputLabel>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <AsyncSelect
                                control={control}
                                name={OperacionContraparteCuentaFields.ImporteConcertacion}
                                label='Límite Concertación'
                                loadOptions={() => HttpCacheOperacion.getImportesPorMoneda(monedaSeleccionada)}
                                fullWidth
                                valueField={OperacionContraparteImporteFields.ContraparteImporte}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AsyncSelect
                                control={control}
                                name={OperacionContraparteCuentaFields.ImporteDPV}
                                label='Límite DVP'
                                loadOptions={() => HttpCacheOperacion.getImportesPorMoneda(monedaSeleccionada)}
                                fullWidth
                                valueField={OperacionContraparteImporteFields.ContraparteImporte}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AsyncSelect
                                control={control}
                                name={OperacionContraparteCuentaFields.ImporteFOP}
                                label='Límite FOP'
                                loadOptions={() => HttpCacheOperacion.getImportesPorMoneda(monedaSeleccionada)}
                                fullWidth
                                valueField={OperacionContraparteImporteFields.ContraparteImporte}
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant='text' color='primary' onClick={() => setOpen(false)}>
                    Cancelar
                </Button>
                <Button variant='contained' color='primary' onClick={handleSubmit(alGuardarDetalle)}>
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
